import React from 'react';
import IntelligenceBlogPost from './IntelligenceBlogPost';

const ContentStrategy = () => {
  const content = [
    {
      heading: "Why Most Content Strategy Fails",
      paragraphs: [
        "Let's be honest: most 'content strategies' are just glorified editorial calendars. Post 3x per week. Mix of educational and promotional. Engage with comments. Rinse, repeat, wonder why nothing's working.",
        "The problem isn't execution—it's the premise. Traditional content strategy assumes distribution is free and attention is abundant. Neither is true. In 2024, distribution is the strategy. Everything else is tactics.",
        "We've worked with 200+ brands. The ones that win don't have better content—they have better distribution systems. They've engineered their content to work with the algorithm, not against it."
      ]
    },
    {
      heading: "The Distribution-First Framework",
      paragraphs: [
        "Here's the framework: Start with the platform. Understand the algorithm. Reverse-engineer what gets distributed. Then create content that matches. Not the other way around.",
        "YouTube wants watch time. TikTok wants completion rate. LinkedIn wants dwell time. Instagram wants saves. Each platform has different incentives. Your content strategy should be platform-specific, not platform-agnostic.",
        "This doesn't mean 'gaming the algorithm.' It means respecting the rules of the game. The algorithm isn't your enemy—it's your distribution partner. Work with it."
      ]
    },
    {
      heading: "The Content Pyramid",
      paragraphs: [
        "Most brands create content horizontally: 10 mediocre pieces spread across 10 topics. Wrong approach. Create vertically: 10 pieces of content about ONE topic, each going deeper than the last.",
        "We call this the Content Pyramid. Base: Short-form content (60s). Middle: Medium-form content (5-10min). Top: Long-form content (20min+). Each layer feeds the next. Each layer targets a different stage of awareness.",
        "The magic happens when someone watches your 60s clip, then your 10min video, then your 20min deep-dive. By the end, they're not just aware of you—they trust you. That's when they buy."
      ]
    },
    {
      heading: "Founder-Led Content: The Unfair Advantage",
      paragraphs: [
        "Here's a controversial take: your brand can't build trust. Only people can. And in B2B, the person who should be building that trust is the founder.",
        "Founder-led content isn't about ego—it's about authenticity. People don't trust brands. They trust people. When the founder shows up, shares their process, admits their mistakes, and teaches what they've learned, trust compounds.",
        "We've seen this play out dozens of times. A generic brand account gets 1,000 views per video. The founder's personal account gets 50,000 views with the same content. Why? Because people follow people, not logos."
      ]
    },
    {
      heading: "The Compounding Content Playbook",
      paragraphs: [
        "Most content has a shelf life of 48 hours. Post it, get some engagement, move on. That's a treadmill. You need content that compounds—content that gets more valuable over time.",
        "Evergreen content compounds. Educational content compounds. Controversial takes compound. Memes don't compound. News doesn't compound. Choose wisely.",
        "Here's the playbook: Create 1 piece of pillar content per month. Something deep, valuable, and evergreen. Then create 10 pieces of derivative content from that pillar. Clips, quotes, threads, carousels. Distribute everywhere. Repeat."
      ]
    },
    {
      heading: "Measuring What Matters",
      paragraphs: [
        "Vanity metrics are killing your content strategy. Likes, followers, impressions—none of it matters if you're not driving business outcomes.",
        "Here's what we measure: Retention rate (are people actually watching?). Click-through rate (are people taking action?). Conversion rate (are people buying?). Everything else is noise.",
        "The best content strategy is the one that drives revenue. If your content isn't contributing to the bottom line, it's not a strategy—it's a hobby."
      ]
    }
  ];

  const keyTakeaways = [
    "Distribution is the strategy. Content is the tactic. Start with the platform, not the idea.",
    "Create content vertically (deep on one topic) not horizontally (shallow on many topics).",
    "Founder-led content builds trust faster than brand content. People follow people, not logos.",
    "Evergreen content compounds. News and memes don't. Choose content that gets more valuable over time.",
    "Measure retention, CTR, and conversion—not likes and followers. Drive business outcomes, not vanity metrics."
  ];

  return (
    <IntelligenceBlogPost
      title="Content Strategy in the Attention Economy"
      category="Strategy"
      readTime="12 min read"
      publishDate="December 10, 2024"
      content={content}
      keyTakeaways={keyTakeaways}
    />
  );
};

export default ContentStrategy;
